import React, { useEffect, useState, useCallback } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { auth, db } from './firebase';
import {
  signOut,
  updateProfile as fbUpdateProfile,
} from 'firebase/auth';
import { 
  doc, 
  setDoc, 
  updateDoc,
  writeBatch
} from 'firebase/firestore';
import { UserContext } from './contexts/UserContext';
import { useAuth } from './hooks/useAuth';

// New and existing page/layout components
import HomePage from './pages/HomePage';
import AuthGate from './components/auth/AuthGate';
import PublicGate from './components/auth/PublicGate'; // New import
import MainLayout from './components/core/MainLayout';
import Login from './components/auth/Login';
import NameVerification from './components/auth/NameVerification';
import CommunityGoalsPage from './pages/CommunityGoalsPage'; // New import

// Other necessary imports

import { NotificationProvider } from './contexts/NotificationContext';
import { Toaster, toast } from 'react-hot-toast';
import { RecordsUpdaterProvider } from './contexts/RecordsUpdaterContext';
import { TutorialProvider } from './contexts/TutorialContext';

function App() {
  const { user, updateUserContext, linkWithGoogle, linkEmailPassword } = useAuth();
  const [error, setError] = useState('');
  const [showCountdown, setShowCountdown] = useState(true);
  const [countdownHeight, setCountdownHeight] = useState(0);
  const navigate = useNavigate();
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');

  useEffect(() => {
    if (user?.theme) {
      setTheme(user.theme);
    }
  }, [user?.theme]);

  useEffect(() => {
    localStorage.setItem('theme', theme);
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  useEffect(() => {
    if (user && typeof user.showCountdown !== 'undefined') {
      setShowCountdown(user.showCountdown);
    }
  }, [user]);

  const handleLogout = useCallback(async () => {
    try {
      if (user && user.currentRoomId) {
        const userRef = doc(db, 'users', user.uid);
        const roomParticipantRef = doc(db, 'rooms', user.currentRoomId, 'participants', user.uid);
        
        const batch = writeBatch(db);
        batch.update(userRef, { currentRoomId: null, isStudying: false, studySpotId: null, studyRoomEntryTime: null });
        batch.delete(roomParticipantRef);
        await batch.commit();
      } else if (user) {
        const userRef = doc(db, 'users', user.uid);
        await updateDoc(userRef, { isStudying: false, studySpotId: null, studyRoomEntryTime: null });
      }
      await signOut(auth);
      navigate('/');
    } catch (err) {
        setError('ログアウトに失敗しました。');
        console.error("Logout error:", err);
    }
  }, [user, navigate]);

  const handleSave = useCallback(async (newUrl) => {
    if (!auth.currentUser) return;
    try {
      await fbUpdateProfile(auth.currentUser, { photoURL: newUrl });
      await setDoc(doc(db, 'users', auth.currentUser.uid), { photoURL: newUrl }, { merge: true });
      updateUserContext({ photoURL: newUrl });
    } catch (err) {
        setError('プロフィール画像の保存に失敗しました。');
    }
  }, [updateUserContext]);

  const handleLinkEmailPassword = useCallback(async (password) => {
    if (!user) return;
    try {
      await linkEmailPassword(user.email, password);
      // The onAuthStateChanged listener in useAuth will eventually update the user object,
      // but we can optimistically update the context to reflect the change immediately.
      const updatedUser = {
        ...user,
        providerData: [...(user.providerData || []), { providerId: 'password' }]
      };
      updateUserContext(updatedUser);
      toast.success('メールアドレスとパスワードを連携しました。');
    } catch (error) {
      console.error("メールアドレスの連携に失敗しました: ", error);
      let message = '連携に失敗しました。';
      if (error.code === 'auth/credential-already-in-use') {
        message = 'このメールアドレスは既に他のアカウントで使用されています。';
      } else if (error.code === 'auth/weak-password') {
        message = 'パスワードは6文字以上である必要があります。';
      }
      toast.error(message);
      throw error; // Re-throw to allow modal to handle loading state
    }
  }, [user, linkEmailPassword, updateUserContext]);

  const handleLinkWithGoogle = useCallback(async () => {
    if (!user) return;
    try {
      await linkWithGoogle();
      const updatedUser = {
        ...user,
        providerData: [...(user.providerData || []), { providerId: 'google.com' }]
      };
      updateUserContext(updatedUser);
      toast.success('Googleアカウントを連携しました。');
    } catch (error) {
      console.error("Googleアカウントの連携に失敗しました: ", error);
      let message = '連携に失敗しました。';
      if (error.code === 'auth/credential-already-in-use') {
        message = 'このGoogleアカウントは既に他のアカウントで使用されています。';
      }
      toast.error(message);
    }
  }, [user, linkWithGoogle, updateUserContext]);

  const handleToggleCountdown = useCallback(async () => {
    if (!user) return;
    const newShowCountdown = !showCountdown;
    setShowCountdown(newShowCountdown);
    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, { showCountdown: newShowCountdown });
    } catch (error) {
      console.error("カウントダウン設定の保存に失敗しました: ", error);
    }
  }, [user, showCountdown]);

  const handleToggleTheme = useCallback(async () => {
    if (!user) return;
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, { theme: newTheme });
      updateUserContext({ theme: newTheme });
    } catch (error) {
      console.error("テーマ設定の保存に失敗しました: ", error);
    }
  }, [user, theme, updateUserContext]);
  
  const mainLayoutElement = (
    <MainLayout 
      user={user}
      onLogout={handleLogout}
      onSave={handleSave}
      onLinkWithGoogle={handleLinkWithGoogle}
      onLinkEmailPassword={handleLinkEmailPassword}
      error={error}
      showCountdown={showCountdown}
      onToggleCountdown={handleToggleCountdown}
      onToggleTheme={handleToggleTheme}
      countdownHeight={countdownHeight}
      setCountdownHeight={setCountdownHeight}
    />
  );

  return (
    <div className={`${theme} bg-background`}>
      <UserContext.Provider value={{ user, updateUserContext }}>
        <NotificationProvider>
          <RecordsUpdaterProvider>
            <TutorialProvider>
              <Toaster />
              <Routes>
                {/* Public routes that redirect if logged in */}
                <Route path="/" element={<PublicGate><HomePage /></PublicGate>} />
                <Route path="/login" element={<PublicGate><Login /></PublicGate>} />

                {/* All other routes are protected by AuthGate */}
                <Route path="/*" element={
                    <AuthGate>
                      <Routes>
                        {/* Special protected route for incomplete profiles */}
                        <Route path="/name-verification" element={<NameVerification />} />

                        {/* Main app layout for complete profiles */}
                        <Route path="/*" element={mainLayoutElement} />
                      </Routes>
                    </AuthGate>
                  }
                />
              </Routes>
            </TutorialProvider>
          </RecordsUpdaterProvider>
        </NotificationProvider>
      </UserContext.Provider>
    </div>
  );
}

export default App;